import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  Rating,
  alpha,
  useTheme,
  Chip,
} from '@mui/material';
import {
  FormatQuote as FormatQuoteIcon,
  WorkspacePremium as WorkspacePremiumIcon,
} from '@mui/icons-material';

interface Testimonial {
  name: string;
  role: string;
  company: string;
  avatar: string;
  rating: number;
  quote: string;
  metric?: {
    value: string;
    label: string;
  };
}

const Testimonials: React.FC = () => {
  const theme = useTheme();

  const testimonials: Testimonial[] = [
    {
      name: 'Sarah Johnson',
      role: 'Engineering Manager',
      company: 'TechCorp Solutions',
      avatar: 'SJ',
      rating: 5,
      quote:
        'TYVRA transformed how our team plans sprints. The AI estimation feature alone saved us 40% of our planning time. It\'s like having a senior PM on every sprint planning call.',
      metric: {
        value: '40%',
        label: 'Time Saved',
      },
    },
    {
      name: 'Michael Chen',
      role: 'Product Owner',
      company: 'Digital Dynamics',
      avatar: 'MC',
      rating: 5,
      quote:
        'The leadership dashboard gives me real-time visibility I never had before. I can spot risks early and make data-driven decisions. Our delivery predictability improved by 60%.',
      metric: {
        value: '60%',
        label: 'Better Predictability',
      },
    },
    {
      name: 'Emily Rodriguez',
      role: 'Scrum Master',
      company: 'Innovation Labs',
      avatar: 'ER',
      rating: 5,
      quote:
        'We migrated from Jira in under an hour. The import was flawless, and our team adopted TYVRA immediately. The interface is so intuitive, we barely needed any training.',
      metric: {
        value: '<1hr',
        label: 'Migration Time',
      },
    },
    {
      name: 'David Park',
      role: 'VP of Engineering',
      company: 'CloudScale Inc',
      avatar: 'DP',
      rating: 5,
      quote:
        'TYVRA scales beautifully. We manage 15 teams and 200+ projects seamlessly. The analytics help us optimize resources across the entire organization.',
      metric: {
        value: '15',
        label: 'Teams',
      },
    },
    {
      name: 'Lisa Thompson',
      role: 'Agile Coach',
      company: 'Startup Ventures',
      avatar: 'LT',
      rating: 5,
      quote:
        'As an agile coach working with multiple companies, I recommend TYVRA to all my clients. It strikes the perfect balance between powerful features and ease of use.',
      metric: {
        value: '10+',
        label: 'Companies',
      },
    },
    {
      name: 'James Wilson',
      role: 'CTO',
      company: 'FinTech Pro',
      avatar: 'JW',
      rating: 5,
      quote:
        'The ROI was immediate. Within the first month, we saw improved team velocity and better sprint completion rates. TYVRA paid for itself in the first quarter.',
      metric: {
        value: '3mo',
        label: 'ROI',
      },
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: theme.palette.background.default,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.03,
          backgroundImage: `radial-gradient(circle at 2px 2px, ${theme.palette.primary.main} 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: 8 }}>
          {/* Section Badge */}
          <Chip
            icon={<WorkspacePremiumIcon />}
            label="LOVED BY TEAMS WORLDWIDE"
            sx={{
              bgcolor: alpha(theme.palette.success.main, 0.1),
              color: theme.palette.success.dark,
              fontWeight: 700,
              fontSize: '0.875rem',
              px: 1,
            }}
          />

          {/* Section Title */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 800,
              maxWidth: '800px',
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Trusted by Agile Teams
          </Typography>

          {/* Section Subtitle */}
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: '700px',
              fontWeight: 400,
              lineHeight: 1.7,
            }}
          >
            See what teams around the world are saying about TYVRA
          </Typography>

          {/* Overall Rating */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <Rating value={5} readOnly size="large" />
            <Typography variant="h5" fontWeight={700}>
              5.0
            </Typography>
            <Typography variant="body2" color="text.secondary">
              from 500+ reviews
            </Typography>
          </Stack>
        </Stack>

        {/* Testimonials Grid */}
        <Grid container spacing={4}>
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                  position: 'relative',
                  overflow: 'visible',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                  },
                }}
              >
                {/* Quote Icon */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -20,
                    right: 20,
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }}
                >
                  <FormatQuoteIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>

                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Stack spacing={3}>
                    {/* Rating */}
                    <Rating value={testimonial.rating} readOnly size="small" />

                    {/* Quote */}
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.8,
                        fontStyle: 'italic',
                        flexGrow: 1,
                      }}
                    >
                      "{testimonial.quote}"
                    </Typography>

                    {/* Metric Badge */}
                    {testimonial.metric && (
                      <Box
                        sx={{
                          alignSelf: 'flex-start',
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: theme.palette.success.dark,
                            fontWeight: 800,
                            fontSize: '1.25rem',
                          }}
                        >
                          {testimonial.metric.value}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.success.dark,
                            fontWeight: 600,
                          }}
                        >
                          {testimonial.metric.label}
                        </Typography>
                      </Box>
                    )}

                    {/* Author */}
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: theme.palette.primary.main,
                          fontSize: '1.25rem',
                          fontWeight: 700,
                        }}
                      >
                        {testimonial.avatar}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {testimonial.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.875rem',
                          }}
                        >
                          {testimonial.role}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                          }}
                        >
                          {testimonial.company}
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Bottom CTA */}
        <Box
          sx={{
            mt: 8,
            textAlign: 'center',
            p: 4,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
            Join 1,000+ Happy Teams
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Start your free 14-day trial and see why teams love TYVRA
          </Typography>
          <Typography
            component="span"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 700,
              fontSize: '1.125rem',
              cursor: 'pointer',
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              pb: 0.5,
              transition: 'all 0.3s ease',
              '&:hover': {
                color: theme.palette.primary.dark,
                borderColor: theme.palette.primary.dark,
              },
            }}
          >
            Read More Success Stories →
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Testimonials;

